import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { ResumePaymentService } from '@wlp/ui-bs-pix';
import { UiBsTranslateService } from '@wlp/ui-bs-translate';
import { LayoutConfigModel } from 'src/app/core/layout/models/layout-config.model';
import { LayoutConfigService } from 'src/app/core/layout/services/layout-config.service';
import { TransactionStoreAction } from 'src/app/core/store/pix/actions/transaction.store.action';
import { IBeneficiaryDTO, IUserDTO } from '../../../../../../../core/business/pix/transaction/transaction.models';
import { LoadingService } from '../../../../../../../core/layout/services/loading.service';
import { ModalTransferPixComponent } from '../../../../modals/modal-transfer-pix/modal-transfer-pix.component';

@Component({
  selector: 'app-prepare-transaction',
  templateUrl: './prepare-transaction.page.html',
  styleUrls: ['./prepare-transaction.page.scss'],
})
export class PrepareTransactionPage implements OnInit {
  private langDefault: string;
  private layoutConfigValue: LayoutConfigModel;

  private stateManagerData: any;
  private dataTransaction;
  public formResumeValue: FormGroup;
  public showData: { key: string; name: any; document: any; bank: any; agency: any; accountNumber: string; };

  constructor(
    private layoutConfigService: LayoutConfigService,
    private translate: UiBsTranslateService,
    private router: Router,
    private navCtrl: NavController,
    private modalController: ModalController,
    private loadingService: LoadingService,
    private transactionStoreAction: TransactionStoreAction,
    private resumePaymentService: ResumePaymentService,
  ) {}

  public async ngOnInit() {
    this.layoutConfigValue = this.layoutConfigService.getSavedConfig();
    this.langDefault = this.layoutConfigValue.wlTheme.languageDefault;
    this.translate.setDefaultLang(this.langDefault);

    this.formResumeValue = new FormGroup({
      paymentValue: new FormControl('', Validators.required),
      paymentDescription: new FormControl(''),
    });

    this.loadingService.show();

    try {
      await this.initialize();
    } catch (error) {
      console.error('Erro na inicialização:', error);
    } finally {
      this.loadingService.hide();
    }
  }

  public async initialize() {
    this.stateManagerData = await this.transactionStoreAction.read('getPrepareTransactionByKeyPix');

    if (this.stateManagerData === null) {
      this.router.navigate([''], { replaceUrl: true });
      return;
    }

    this.formResumeValue = this.resumePaymentService.getFormResumePayment();

    await this.preparePayment(this.stateManagerData.payload, this.formResumeValue);

    this.showData = {
      key: this.stateManagerData.payload.beneficiary.key,
      name: this.setReceipientIsName(this.stateManagerData.payload),
      document: this.formatDocument(this.stateManagerData.payload.beneficiary.document)[0],
      bank: this.stateManagerData.payload.beneficiary.bank,
      agency: this.stateManagerData.payload.beneficiary.branch,
      accountNumber: this.formatAccountNumber(this.stateManagerData.payload.beneficiary.accountNumber),
    };
  }

  public async validateOperation() {
    this.loadingService.show();
    this.dataTransaction = this.createTransactionData(
      this.stateManagerData.payload,
      this.stateManagerData.pageData.userProfile,
      this.formResumeValue.controls.paymentValue.value,
      this.formResumeValue.controls.paymentDescription.value,
    );
    this.loadingService.hide();
    this.transactionStoreAction.update('setPreTransactionByKeyPix', {
      payload: { ...this.dataTransaction },
      pageData: {
        accountData: this.stateManagerData.payload,
        userProfile: this.stateManagerData.pageData.userProfile,
      },
    });
    return await this.openModalResume();
  }

  public createTransactionData(
    data: { beneficiary: IBeneficiaryDTO },
    user: IUserDTO,
    paymentValue: string,
    paymentDescription: string,
  ) {
    return {
      accountNumber: data.beneficiary.accountNumber,
      accountType: this.formatAccountType(data.beneficiary.accountType),
      agencyPayer: user.agencyBank,
      bankDestination: data.beneficiary.bank,
      branch: data.beneficiary.branch,
      codeAuthorization: '',
      currentAccountPayer: user.numberAccountBank,
      dateSchedule: '',
      devolutionCode: '',
      document: data.beneficiary.document,
      endToEnd: this.getEndToEnd(data.beneficiary.endToEndId),
      isCharged: true,
      ispb: data.beneficiary.ispb,
      key: data.beneficiary.key,
      name: this.getName(data),
      namePayer: user.nameComplete,
      operationCode: '',
      operationValue: parseFloat(paymentValue.replace(',', '.')),
      originNameBank: user.nameBank,
      personType: data.beneficiary.personType,
      pixDescription: paymentDescription,
      primaryDescription: '',
      scheduled: '',
      transactionId: '',
      transactionIdCredit: '',
      uuidAccountPayment: user.uuidAccount,
      uuidWhiteLabel: user.uuidWhiteLabel,
    };
  }

  private setPaymentValueForm(value: number, formResumeValue) {
    const formattedValue = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    formResumeValue.get('paymentValue').setValue(formattedValue);
    return formResumeValue.controls.paymentValue.value;
  }

  private async preparePayment(payment, formResumeValue): Promise<{ paymentValue: any; disableValue: boolean }> {
    const paymentValue =
      typeof payment.value === 'object' && payment.value.fixedValue
        ? this.setPaymentValueForm(payment.value.final, formResumeValue)
        : this.setPaymentValueForm(0, formResumeValue);
    const disableValue = !(payment.value === 0 || payment.value.fixedValue === false);
    return Promise.resolve({
      paymentValue,
      disableValue,
    });
  }

  public checkFormValue() {
    return false;
  }

  public async openModalResume() {
    const modalResume = await this.modalController.create({
      component: ModalTransferPixComponent,
      cssClass: 'modal-select-pix',
      componentProps: {
        dataTransaction: this.dataTransaction,
        stateManagerData: this.stateManagerData,
      },
    });
    return await modalResume.present();
  }

  private setReceipientIsName(data) {
    return data.beneficiary.personType === 'LEGAL_PERSON' && !!data.beneficiary.tradeName
      ? data.beneficiary.tradeName
      : data.beneficiary.name;
  }

  private getEndToEnd(endToEnd: string): string {
    return endToEnd || '';
  }

  private getName(data: { beneficiary: IBeneficiaryDTO }): string {
    return data.beneficiary.personType === 'LEGAL_PERSON' && !!data.beneficiary.tradeName
      ? data.beneficiary.tradeName
      : data.beneficiary.name;
  }

  public formatCnpjCpf(value) {
    const cnpjCpf = value.replace(/\D/g, '');
    if (cnpjCpf.length === 11) {
      return cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4');
    }
    return cnpjCpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, '$1.$2.$3/$4-$5');
  }

  public formatDocument(document: string) {
    if (document.length >= 14) {
      return [this.formatCnpjCpf(document), 'PIX.PAYMENT_RESUME.RECIPIENT_INFORMATION.CNPJ'];
    } else {
      return [document, 'PIX.PAYMENT_RESUME.RECIPIENT_INFORMATION.CPF'];
    }
  }

  public formatAccountType(type: string): string {
    switch (type) {
      case 'CP':
        return 'CP';
      case 'TRAN':
        return 'CP';
      case 'PP':
        return 'PP';
      case 'PG':
        return 'PP';
      case 'SVGS':
        return 'PP';
      case 'CC':
        return 'CC';
      case 'CACC':
        return 'CC';
      case 'CS':
        return 'CS';
      case 'SLRY':
        return 'CS';
    }
  }

  public formatAccountNumber(accountNumber: string): string {
    return accountNumber.replace(/[-/.]/g, '');
  }

  public goBack() {
    this.navCtrl.navigateRoot('/app/dashboard');
  }
}
